/* import __COLOCATED_TEMPLATE__ from './custom-report-header.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type Report from 'embercom/models/reporting/custom/report';
import type IntlService from 'embercom/services/intl';
import type FavoritesService from 'embercom/services/favorites-service';
import { taskFor } from 'ember-concurrency-ts';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type Chart from 'embercom/models/reporting/custom/chart';
import { type TaskGenerator } from 'ember-concurrency';
import type Router from '@ember/routing/router-service';
import type CustomReportsService from 'embercom/services/custom-reports-service';
import type ReportAccessService from 'embercom/services/report-access-service';
import type Transition from '@ember/routing/transition';
import Run from 'embercom/lib/run';
import type ReportingStandalone from 'embercom/services/reporting-standalone';
import type ReportingAgentSidebarService from 'embercom/services/reporting-agent-sidebar';
import type ReportingViews from 'embercom/services/reporting-views';
import type BpoAccessControlService from 'embercom/services/bpo-access-control';
import { BPO_REPORTING_HELP_URL } from 'embercom/services/bpo-access-control';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';

interface Args {
  editMode: boolean;
  enableEditMode: (value: boolean) => Promise<Transition>;
  sharedAnalyticsData: Record<string, string>;
  report: Report;
  chart: Chart;
  duplicateReport: () => TaskGenerator<void>;
  deleteReport: () => TaskGenerator<void>;
  saveReport: () => TaskGenerator<void>;
  addChart: (buttonPosition: string) => Promise<void>;
  openChartTemplateSidePanel: () => void;
  closeChartTemplateSidePanel: () => void;
  sidePanelIsOpen: boolean;
  cancelChanges: () => void;
  referrer: string;
  hasChanges: () => boolean;
  resetState: () => boolean;
  isStandalone: boolean;
  openShareSideDrawer: () => void;
  openExternalSharingSideDrawer?: () => void;
  openScheduleDeliveryDrawer?: () => void;
  reportState: ReportState;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

export default class ReportingCustomReportCustomReportHeader extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare favoritesService: FavoritesService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service intercomEventService: $TSFixMe;
  @service permissionsService: $TSFixMe;
  @service appService: $TSFixMe;
  @service declare router: Router;
  @service declare customReportsService: CustomReportsService;
  @service declare reportAccessService: ReportAccessService;
  @service declare reportingViews: ReportingViews;
  @service declare modalService: $TSFixMe;
  @service declare reportingStandalone: ReportingStandalone;
  @service declare bpoAccessControl: BpoAccessControlService;
  @service declare reportingAgentSidebar: ReportingAgentSidebarService;

  @action
  openReportingAgent() {
    this.reportingAgentSidebar.openWithReport(this.args.report, this.args.reportState);
  }

  @action
  trackAnalyticsEvent(data: Record<string, string>) {
    this.intercomEventService.trackAnalyticsEvent({
      ...this.args.sharedAnalyticsData,
      ...data,
    });
  }

  @action
  async addChart() {
    await this.args.addChart('header');
  }

  get confirmCancelText() {
    return {
      title: this.intl.t('reporting.custom-reports.report.cancel-modal.title'),
      primaryButtonType: 'primary',
      confirmButtonText: this.intl.t(
        'reporting.custom-reports.report.cancel-modal.confirm-btn-text',
      ),
      cancelButtonText: this.intl.t('reporting.custom-reports.report.cancel-modal.cancel-btn-text'),
      body: this.intl.t('reporting.custom-reports.report.cancel-modal.body'),
      distinguishCancelFromClose: false,
    };
  }

  get confirmDuplicatedTitleText() {
    let otherReport = this.reportWithSameTitle!;
    return {
      title: this.intl.t('reporting.custom-reports.report.duplicated-title-modal.title'),
      body: this.intl.t('reporting.custom-reports.report.duplicated-title-modal.body', {
        reportTitle: otherReport.name,
        reportLink: this.router.urlFor(otherReport.route, otherReport.id),
        reportCreator: otherReport.author?.name || this.intl.t('reporting.intercom-report-author'),
        htmlSafe: true,
      }),
      confirmButtonText: this.intl.t(
        'reporting.custom-reports.report.duplicated-title-modal.confirm',
      ),
    };
  }

  get addChartOptions() {
    return [
      {
        items: [
          {
            text: this.intl.t('reporting.custom-reports.report.from-template'),
            onSelectItem: this.args.openChartTemplateSidePanel,
            isDisabled: this.args.sidePanelIsOpen,
          },
          {
            text: this.intl.t('reporting.custom-reports.report.create-new-chart'),
            onSelectItem: this.addChart,
          },
        ],
      },
    ];
  }

  get reportWithSameTitle() {
    return this.reportingViews.displayableReports.find(
      (report) => report.name === this.args.report.title,
    );
  }

  @action
  addOrRemoveFavorite() {
    taskFor(this.favoritesService.addOrRemoveFavorite).perform(this.args.report.id);
  }

  @action
  async saveReport() {
    let isNewReport = this.reportIsNew(this.args.report);

    if (isNewReport && this.args.report.hasTitle && this.reportWithSameTitle) {
      let confirmed: boolean = await this.intercomConfirmService.confirm(
        this.confirmDuplicatedTitleText,
      );
      if (!confirmed) {
        return;
      }
    }

    // the order of these three matter. saving reports should be first.
    await taskFor(this.args.saveReport).perform();
    await this.args.enableEditMode(false);
    this.args.closeChartTemplateSidePanel();
    if (isNewReport) {
      Run.later(
        this,
        () => {
          this.customReportsService.openSharingModal(this.args.report, true);
        },
        1600,
      ); // this delay is added to improve UI interaction and create a lag for when modal pops up.
    }
  }

  @action
  navigateToSourceRoute() {
    if (this.args.referrer === this.router.currentURL) {
      //ifs previous route is same as current route take user to overview page.
      this.router.transitionTo('apps.app.reports.overview');
    } else {
      this.router.transitionTo(this.args.referrer);
    }
  }

  reportIsNew(report: Report) {
    return report.isNew as unknown as boolean;
  }

  private closeSidePanelIfOpen() {
    if (this.args.sidePanelIsOpen) {
      this.args.closeChartTemplateSidePanel();
    }
  }

  @action
  async cancel() {
    if (this.args.report.isNewAndUnChanged) {
      this.closeSidePanelIfOpen();
      this.args.cancelChanges();
      this.navigateToSourceRoute();
    } else if (this.args.report.fromTemplate && this.reportIsNew(this.args.report)) {
      let confirmed: boolean = await this.intercomConfirmService.confirm(this.confirmCancelText);
      if (confirmed) {
        this.closeSidePanelIfOpen();
        this.args.cancelChanges();
        this.args.resetState(); //reset all route state to avoid https://github.com/intercom/embercom/pull/88930/files#r1783327619
        this.navigateToSourceRoute();
      }
    } else if (this.args.hasChanges()) {
      let confirmed: boolean = await this.intercomConfirmService.confirm(this.confirmCancelText);
      if (confirmed && !this.reportIsNew(this.args.report)) {
        await this.args.report.reload();
        this.args.cancelChanges();
        this.closeSidePanelIfOpen();
        this.args.enableEditMode(false);
      } else if (confirmed && this.reportIsNew(this.args.report)) {
        this.args.cancelChanges();
        this.closeSidePanelIfOpen();
        this.navigateToSourceRoute();
      }
    } else {
      this.closeSidePanelIfOpen();
      this.args.enableEditMode(false);
    }
  }

  get favouriteItem() {
    if (this.favoritesService.isFavoriteReport(this.args.report.id)) {
      return {
        text: this.intl.t('reporting.custom-reports.report.remove-from-favorites'),
        icon: 'relationship',
        onSelectItem: this.addOrRemoveFavorite,
      };
    }
    return {
      text: this.intl.t('reporting.custom-reports.report.add-to-favorites'),
      icon: 'relationship-light',
      onSelectItem: this.addOrRemoveFavorite,
    };
  }

  get actionItems() {
    return [
      {
        items: [
          this.favouriteItem,
          {
            text: this.intl.t('reporting.custom-reports.report.duplicate'),
            icon: 'duplicate',
          },
          {
            text: this.intl.t('reporting.custom-reports.report.delete'),
            icon: 'trash',
            isDestructive: true,
          },
        ],
      },
    ];
  }

  private get canEdit() {
    return this.customReportsService.canChangeCustomReports;
  }

  @action
  onClickEditReport() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      clicked_property: 'edit_btn',
      object: 'custom_report',
      has_edit_permission: this.canEdit,
      section: 'reports',
    });

    if (this.canEdit) {
      this.args.enableEditMode(true);
    } else {
      this.customReportsService.loadChangePermissionModal();
    }
  }

  get updatedAt(): Date {
    return this.args.report.updatedAt;
  }

  get createdBy(): string | undefined {
    return this.args.report.createdByAdmin?.name;
  }
  get createdByModel() {
    return this.args.report.createdByAdmin;
  }

  get disableSave() {
    //disable save if report is dirty
    return !this.args.hasChanges();
  }

  get editReportDisabled(): boolean {
    return !this.reportAccessService.adminIsCreatorOrHasEditAccess(this.args.report);
  }

  get shouldShowOwnerLabel() {
    let renderUpdatedBy = !this.args.editMode;
    if (this.args.isStandalone) {
      return this.reportingStandalone.supportsFeature('showReportOwner') && renderUpdatedBy;
    }
    return renderUpdatedBy;
  }

  get shouldShowLimitedMetricsTooltip() {
    return this.bpoAccessControl.hasLimitedReportingAccess && !this.args.editMode;
  }

  get learnMoreUrl(): string {
    return BPO_REPORTING_HELP_URL;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::Report::CustomReportHeader': typeof ReportingCustomReportCustomReportHeader;
    'reporting/custom/report/custom-report-header': typeof ReportingCustomReportCustomReportHeader;
  }
}
